import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { services } from '../data/services';
import { ProductCard } from '../components/ProductCard';
import { useCatalog } from '../lib/catalog';
import { IndustriesCarousel } from '../components/IndustriesCarousel';
import { StatsBand } from '../components/StatsBand';
import { ModernTimeline } from '../components/ModernTimeline';
import { RealSolutions } from '../components/RealSolutions';

export function HomePage() {
  const [status, setStatus] = useState<string | null>(null);
  const { products } = useCatalog();
  const featured = products.filter((p) => p.featured).slice(0, 4);
  const lineup = featured.length ? featured : products.slice(0, 4);

  function handleContact(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const name = String(data.get('name') || '').trim();
    setStatus(`Thanks${name ? `, ${name}` : ''}! We'll be in touch within one business day.`);
    e.currentTarget.reset();
  }

  return (
    <>
      <section className="hero">
        <video className="hero-video" autoPlay loop muted playsInline>
          <source src="https://imgs.bizioffice.com/website/Hero%20brain4k.mp4" type="video/mp4" />
        </video>
        <div className="container hero-inner">
          <div className="eyebrow">Bizi Office</div>
          <h1 className="hero-title">
            <span className="hero-title-line">Automate your Business</span>
            <span className="gradient-text hero-title-line">Free Your Time</span>
            <span className="hero-title-line">Let Your Company Grow</span>
          </h1>
          <p className="hero-sub">
            AI-powered solutions that eliminate your busy work. Experts in automations that save time. Here for
            production-ready EspoCRM extensions? Check out Bizi Marketplace!
          </p>
          <div className="hero-actions">
            <a className="btn btn-primary btn-lg" href="#contact">
              Schedule a Free Consultation
            </a>
            <Link className="btn btn-ghost btn-lg" to="/marketplace">
              Browse Bizi Marketplace
            </Link>
          </div>
          <div className="hero-stats">
            <div className="hero-stat">
              <strong>Up to 60%</strong>
              <span>Reduction in Operational Costs</span>
            </div>
            <div className="hero-stat">
              <strong>2-3 Weeks</strong>
              <span>You Will Feel Results</span>
            </div>
            <div className="hero-stat">
              <strong>{products.length ? `${products.length} Extensions` : 'Extensions'}</strong>
              <span>EspoCRM Extensions in Market</span>
            </div>
          </div>
        </div>
      </section>

      <section id="services" className="section services">
        <div className="container">
          <div className="section-header">
            <span className="eyebrow">What We Do</span>
            <h2>Custom AI & Automation Solutions</h2>
            <p>
              We're not a SaaS platform. We're your dedicated automation partner, building custom solutions tailored to
              your unique business needs.
            </p>
          </div>
          <div className="grid-3">
            {services.map((service) => (
              <Link className="card service-card service-card-link" to={`/services#${service.slug}`} key={service.id}>
                <div className="service-icon">
                  <span className="material-symbols-outlined">{service.icon}</span>
                </div>
                <h3>{service.name}</h3>
                <p>{service.summary}</p>
              </Link>
            ))}
          </div>
          <div style={{ marginTop: '1.25rem' }}>
            <Link className="btn btn-primary" to="/services">
              Explore all services
            </Link>
          </div>
        </div>
      </section>

      <section id="industries" className="section section-alt">
        <div className="container">
          <div className="section-header">
            <span className="eyebrow">Industries We Serve</span>
            <h2>Automations built for your sector</h2>
            <p>
              We don't try to be everything to everyone. These are the industries where our automation work hits hardest
              — where the ROI is clearest and the time savings are immediate.
            </p>
          </div>
          <IndustriesCarousel />
        </div>
      </section>

      <StatsBand />

      <section id="use-cases" className="section section-alt-2">
        <div className="container">
          <div className="section-header">
            <span className="eyebrow">Real Solutions</span>
            <h2>Sound familiar?</h2>
            <p>
              These are the real pain points we hear on every discovery call. Click through to see exactly how we turn
              each headache into hours back on your calendar.
            </p>
          </div>
          <RealSolutions />
        </div>
      </section>

      <section id="process" className="section">
        <div className="container">
          <div className="section-header">
            <span className="eyebrow">How We Work</span>
            <h2>From consultation to automation</h2>
            <p>
              A clear, proven path from first conversation to a live system your team owns. No black boxes, no
              surprises — just a timeline you can see from start to finish.
            </p>
          </div>
          <ModernTimeline />
          <div className="timeline-cta-row">
            <Link className="btn btn-primary" to="/how-it-works">
              See How We Work
            </Link>
          </div>
        </div>
      </section>

      <section className="section" id="marketplace-preview">
        <div className="container">
          <div className="section-header">
            <span className="eyebrow">Bizi Marketplace</span>
            <h2>EspoCRM extensions, game-day ready</h2>
            <p>
              Digital products built in-house — themes, enum power-ups, list-view firepower, and a live theme engine.
              Secure Stripe™ checkout. Instant download after payment.
            </p>
          </div>
          <div className="product-grid">
            {lineup.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
          <div style={{ marginTop: '1.25rem' }}>
            <Link className="btn btn-primary" to="/marketplace">
              View full catalog
            </Link>
          </div>
        </div>
      </section>

      <section className="section section-alt" id="why-bizi">
        <div className="container">
          <div className="section-header">
            <span className="eyebrow">Why Bizi</span>
            <h2>Why Choose Bizi Office over DIY?</h2>
          </div>

          <div className="card mission-banner" aria-label="Our mission">
            <div className="mission-banner-glow" aria-hidden="true" />
            <div className="mission-banner-content">
              <span className="mission-banner-kicker">Our Mission</span>
              <p className="mission-banner-text">
                To maximize efficiency and reduce costs, freeing you to focus on what matters most: scaling your
                vision.
              </p>
            </div>
          </div>

          <div className="why-grid">
            {[
              [
                'No Learning Curve',
                'There is no time wasted on learning complicated processes and concepts. The headache and the labor is taken out of your hands and into us. Let the professionals save you some time!',
              ],
              [
                'Custom Solutions',
                'Not generic templates — tailored solutions built for your business. Every tool and process is catered directly to your workflow. These tools are designed for you to fix your needs. There is no need to change how you work!',
              ],
              [
                'Guided Strategy',
                'We look for gaps in process or burdens on daily labor. We then strategize an approach with you to tackle those burdens. Every strategy is created with your input and guidance.',
              ],
              [
                'Full Support',
                'We provide support throughout the entire process and after! Our customer support team is here to ensure that there is not an end to our support! Have questions or issues with your workflow? Let us know!',
              ],
              [
                'Proven ROI',
                'You will feel the impact of our tools immediately. Most clients recoup their investment within 60 days of delivery.',
              ],
              [
                'Risk-Free Start',
                'We offer a free consultation to see if we are a fit for you! We will walk through concepts, problems, and possible options of solutions. No charge, and no catch!',
              ],
            ].map(([t, b]) => (
              <div className="card why-item" key={t}>
                <p>
                  <strong>{t}:</strong> {b}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="section contact-section">
        <div className="container">
          <div className="card contact-panel">
            <h2>Ready to eliminate busy work?</h2>
            <p>Schedule a free consultation — or shop ready-made EspoCRM extensions on Bizi Marketplace.</p>
            <form className="contact-form" onSubmit={handleContact}>
              <div className="form-row">
                <input name="name" placeholder="Your Name *" required />
                <input type="email" name="email" placeholder="Email Address *" required />
              </div>
              <div className="form-row">
                <input name="phone" placeholder="Phone Number" />
                <input name="company" placeholder="Company Name" />
              </div>
              <textarea name="message" placeholder="Tell us about your biggest time-wasters... *" rows={4} required />
              <button type="submit" className="btn btn-primary btn-lg">
                Get Your Free Consultation
              </button>
              {status && <p className="form-status">{status}</p>}
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
